import Dexie from 'dexie';
import { db, type SyncOutboxRow } from '../db';
import { newId } from '../../utils/ids';
import { isSupabaseSyncEnabled } from '../supabase/config';
import { REPORT_ATTACHMENT_OUTBOX_TABLE } from './syncConstants';

export type SyncQueueCounts = {
  reports: number;
  contacts: number;
  photos: number;
};

function hasOutboxTable(): boolean {
  return db.tables.some((table) => table.name === 'syncOutbox');
}

function hasCompositeIndex(): boolean {
  return db.syncOutbox.schema?.indexes?.some((index) => index.name === '[table+recordId]') ?? false;
}

function containsBlob(value: unknown, seen = new Set<object>()): boolean {
  if (!value || typeof value !== 'object') return false;
  if (typeof Blob !== 'undefined' && value instanceof Blob) return true;
  if (typeof File !== 'undefined' && value instanceof File) return true;
  if (seen.has(value)) return false;
  seen.add(value);

  if (Array.isArray(value)) {
    return value.some((entry) => containsBlob(entry, seen));
  }

  for (const entry of Object.values(value)) {
    if (containsBlob(entry, seen)) return true;
  }

  return false;
}

export function normalizeOutboxPayload(payload: unknown): unknown {
  if (containsBlob(payload)) {
    throw new Error(
      'Outbox payload must be JSON-serializable and cannot include Blob/File values.'
    );
  }

  const serialized = JSON.stringify(payload);
  if (serialized === undefined) {
    throw new Error('Outbox payload must be JSON-serializable.');
  }

  return JSON.parse(serialized) as unknown;
}

type QueueOptions = {
  coalesce?: boolean;
  idempotencyKey?: string;
};

/**
 * Check if the current transaction already includes syncOutbox table.
 */
function isInSyncOutboxTransaction(): boolean {
  const tx = Dexie.currentTransaction;
  if (!tx) return false;
  return tx.storeNames.includes('syncOutbox');
}

/**
 * Internal function to perform the actual outbox operations.
 */
async function performQueueOperations(
  table: string,
  recordId: string,
  operation: 'upsert' | 'delete',
  normalizedPayload: unknown,
  coalesce: boolean,
  idempotencyKey: string | undefined
): Promise<void> {
  const now = new Date().toISOString();

  if (coalesce) {
    if (hasCompositeIndex()) {
      await db.syncOutbox.where('[table+recordId]').equals([table, recordId]).delete();
    } else {
      await db.syncOutbox
        .filter((row) => row.table === table && row.recordId === recordId)
        .delete();
    }
  }

  const entry: SyncOutboxRow = {
    id: newId(),
    idempotencyKey: idempotencyKey ?? newId(),
    table,
    recordId,
    operation,
    payload: normalizedPayload,
    createdAt: now,
    retryCount: 0,
    lastError: undefined,
  };

  await db.syncOutbox.add(entry);
}

/**
 * Queue a record for sync when online.
 * Replaces any existing entry for the same table/recordId.
 *
 * If called within a transaction that already includes syncOutbox,
 * operates directly within that transaction. Otherwise, creates
 * a separate transaction to avoid blocking the caller.
 */
export async function queueForSync(
  table: string,
  recordId: string,
  operation: 'upsert' | 'delete',
  payload: unknown,
  options: QueueOptions = {}
): Promise<void> {
  if (!hasOutboxTable()) return;

  const normalizedPayload = normalizeOutboxPayload(payload);
  const { coalesce = true, idempotencyKey } = options;

  // If we're already in a transaction that includes syncOutbox,
  // operate directly without creating a nested transaction
  if (isInSyncOutboxTransaction()) {
    await performQueueOperations(
      table,
      recordId,
      operation,
      normalizedPayload,
      coalesce,
      idempotencyKey
    );
    return;
  }

  // Otherwise, use ignoreTransaction to create a separate transaction
  await Dexie.ignoreTransaction(async () => {
    await db.transaction('rw', db.syncOutbox, async () => {
      await performQueueOperations(
        table,
        recordId,
        operation,
        normalizedPayload,
        coalesce,
        idempotencyKey
      );
    });
  });
}

/**
 * Get all pending sync operations ordered by creation time.
 */
export async function getPendingSyncOperations(): Promise<SyncOutboxRow[]> {
  if (!isSupabaseSyncEnabled()) return [];
  return db.syncOutbox.orderBy('createdAt').toArray();
}

/**
 * Get count of pending sync operations.
 */
export async function getPendingSyncCount(): Promise<number> {
  if (!isSupabaseSyncEnabled()) return 0;
  return db.syncOutbox.count();
}

/**
 * Get pending sync counts by queue type.
 */
export async function getPendingSyncCounts(): Promise<SyncQueueCounts> {
  if (!isSupabaseSyncEnabled()) {
    return { reports: 0, contacts: 0, photos: 0 };
  }

  const [reports, contacts, photos] = await Promise.all([
    db.syncOutbox.where('table').equals('reports').count(),
    db.syncOutbox.where('table').equals('contacts').count(),
    db.syncOutbox.where('table').equals(REPORT_ATTACHMENT_OUTBOX_TABLE).count(),
  ]);

  return { reports, contacts, photos };
}

/**
 * Mark a sync operation as failed and increment retry count.
 */
export async function markSyncFailed(
  id: string,
  error: string,
  options?: { retryCount?: number }
): Promise<void> {
  const entry = await db.syncOutbox.get(id);
  if (entry) {
    await db.syncOutbox.update(id, {
      retryCount: options?.retryCount ?? entry.retryCount + 1,
      lastError: error,
    });
  }
}

/**
 * Remove a successfully synced operation from the outbox.
 */
export async function removeSyncedOperation(id: string): Promise<void> {
  await db.syncOutbox.delete(id);
}

/**
 * Remove any outbox entry for a specific table/recordId pair.
 */
export async function removeOutboxEntryByRecord(table: string, recordId: string): Promise<void> {
  if (!hasOutboxTable()) return;
  if (hasCompositeIndex()) {
    await db.syncOutbox.where('[table+recordId]').equals([table, recordId]).delete();
    return;
  }

  const entries = await db.syncOutbox
    .filter((row) => row.table === table && row.recordId === recordId)
    .toArray();
  await Promise.all(entries.map((entry) => db.syncOutbox.delete(entry.id)));
}

/**
 * Clear all pending sync operations (use with caution).
 */
export async function clearSyncOutbox(): Promise<void> {
  await db.syncOutbox.clear();
}

/**
 * Reset retry count for stale failed entries, allowing them to be retried.
 * Entries older than the threshold that have exhausted retries will be reset.
 */
/**
 * Tables that no longer sync and should have their outbox entries cleared.
 */
const DISABLED_SYNC_TABLES = new Set(['templates']);

/**
 * Clear outbox entries for tables that no longer sync.
 * Called during sync initialization to clean up stale entries.
 */
export async function clearDisabledTableEntries(): Promise<number> {
  if (!isSupabaseSyncEnabled()) return 0;

  let deletedCount = 0;
  for (const tableName of DISABLED_SYNC_TABLES) {
    const count = await db.syncOutbox.where('table').equals(tableName).delete();
    deletedCount += count;
  }

  if (deletedCount > 0) {
    console.log(`[SyncOutbox] Cleared ${deletedCount} stale entries for disabled sync tables`);
  }

  return deletedCount;
}

/**
 * Reset stale entries that have exhausted retries.
 * Entries older than the threshold that have exhausted retries will be reset.
 */
export async function resetStaleRetries(
  maxRetries: number,
  staleThresholdMs: number = 60 * 60 * 1000 // 1 hour default
): Promise<number> {
  if (!isSupabaseSyncEnabled()) return 0;

  const now = Date.now();
  const entries = await db.syncOutbox.toArray();

  let resetCount = 0;
  for (const entry of entries) {
    if (entry.retryCount < maxRetries) continue;

    const createdAt = Date.parse(entry.createdAt);
    if (Number.isNaN(createdAt)) continue;

    const age = now - createdAt;
    if (age >= staleThresholdMs) {
      await db.syncOutbox.update(entry.id, {
        retryCount: 0,
        lastError: `Reset after ${Math.round(age / 60000)} minutes. Previous error: ${entry.lastError ?? 'unknown'}`,
      });
      resetCount += 1;
    }
  }

  return resetCount;
}
